import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Pressable, Modal } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import Images from '@themes/images';
import theme from '@themes/theme';

export interface ProjectLimitReachedModalProps {
  iconName?: keyof typeof Images;
  title?: string;
  description: string;
  buttonTitle: string;
  onPrimary: () => void;
  onDismiss?: () => void;
  iconSize?: number;
}

export function ProjectLimitReachedModal({
  onPrimary,
  iconName,
  title,
  description,
  buttonTitle,
  onDismiss,
  iconSize = 64,
}: Readonly<ProjectLimitReachedModalProps>) {
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const cardScale = useRef(new Animated.Value(0.88)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const actionsAnim = useRef(new Animated.Value(0)).current;
  const actionsTranslateY = actionsAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [10, 0],
  });

  const insets = useSafeAreaInsets();

  // Leading-edge debounce so a rapid double-tap on the CTA can't fire
  // onPrimary twice; the ref keeps the wrapper reading the fresh callback.
  const onPrimaryRef = useRef(onPrimary);
  onPrimaryRef.current = onPrimary;
  const handlePrimaryPress = useMemo(
    () =>
      debounce(() => onPrimaryRef.current(), 1000, {
        leading: true,
        trailing: false,
      }),
    []
  );
  useEffect(() => () => handlePrimaryPress.cancel(), [handlePrimaryPress]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(backdropOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.spring(cardScale, {
        toValue: 1,
        damping: 20,
        stiffness: 260,
        useNativeDriver: true,
      }),
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      // CTA appears shortly after checkmark finishes (40ms delay + 220ms draw = 260ms)
      Animated.timing(actionsAnim, {
        toValue: 1,
        duration: 150,
        delay: 300,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  return (
    <Modal visible transparent animationType='none' onRequestClose={onDismiss}>
      {/* Backdrop */}
      <Pressable
        onPress={onDismiss}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
        }}>
        <Animated.View
          style={
            {
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.45)',
              opacity: backdropOpacity,
            } as any
          }
        />
      </Pressable>
      <Animated.View
        style={
          {
            position: 'absolute',
            bottom: 40 + insets.bottom,
            left: 16,
            right: 16,
            backgroundColor: theme.colors.white,
            borderRadius: 20,
            paddingTop: 40,
            paddingBottom: 40,
            overflow: 'hidden',
            zIndex: 60,
            opacity: cardOpacity,
            transform: [{ scale: cardScale }],
          } as any
        }>
        {/* Icon and title are optional so the modal doubles as a plain
            message dialog (e.g. receipt-in-use warning) */}
        {iconName && (
          <Box style={{ alignItems: 'center' }} mb='xxs'>
            <Box
              alignItems='center'
              justifyContent='center'
              style={{ borderRadius: 13.6 }}
              backgroundColor='greenSecondaryMild'
              width={iconSize}
              height={iconSize}>
              <Icon name={iconName} variant='xxl' color='greenSecondary'></Icon>
            </Box>
          </Box>
        )}
        <Box px='l' mt='m' alignItems='center' mb='xxs'>
          {title && (
            <Text variant='heading2' color='textPrimary' textAlign='center'>
              {title}
            </Text>
          )}
          <Text
            variant='body1'
            mt='xs'
            color='textSecondary'
            textAlign='center'>
            {(() => {
              const text =
                description ??
                "You've used all 3 free projects. Subscribe to create unlimited projects.";
              const keyword = 'tasktag.com.';
              const idx = text.indexOf(keyword);
              if (idx === -1) return text;
              return (
                <>
                  {text.slice(0, idx)}
                  <Text variant='labelEmphasizedChatDetialTitle'>
                    {keyword}
                  </Text>
                  {text.slice(idx + keyword.length)}
                </>
              );
            })()}
          </Text>
        </Box>
        <Animated.View
          style={
            {
              marginHorizontal: theme.spacing.l,
              marginTop: theme.spacing.m,
              gap: theme.spacing.xs,
              opacity: actionsAnim,
              transform: [{ translateY: actionsTranslateY }],
            } as any
          }>
          <Pressable
            onPress={handlePrimaryPress}
            style={{
              backgroundColor: theme.colors.black,
              borderRadius: theme.borderRadii.xs,
              paddingVertical: 14,
              alignItems: 'center',
            }}>
            <Text variant='webSmall' color='white'>
              {buttonTitle ?? 'Subscribe'}
            </Text>
          </Pressable>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
